import dayjs from "dayjs";

/**
 * Demo / meeting bookings on a lead.
 *
 * A lead is a demo booking only when `demo_date` is set — every other demo field
 * can be null on a perfectly valid booking, so nothing else is a reliable test.
 * The meeting is Microsoft Teams; never label it Meet.
 *
 * `demo_event_id` is internal and must never reach the screen.
 */

export const hasDemo = (lead) => Boolean(lead?.demo_date);

/** Booking slots — mirrors the public booking form. */
export const DEMO_TIME_SLOTS = [
  "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM",
  "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM",
  "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM",
  "6:00 PM", "6:30 PM", "7:00 PM",
];

const STATUS_STYLES = {
  scheduled: { bg: "#ecfeff", color: "#0e7490", border: "#a5f3fc", label: "Scheduled" },
  confirmed: { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0", label: "Confirmed" },
  pending:   { bg: "#fffbeb", color: "#b45309", border: "#fde68a", label: "Pending" },
  done:      { bg: "#f5f5f5", color: "#6b7280", border: "#e5e7eb", label: "Done" },
  cancelled: { bg: "#fef2f2", color: "#dc2626", border: "#fecaca", label: "Cancelled" },
};

export const demoStatusStyle = (status) => {
  const key = String(status || "").trim().toLowerCase();
  return (
    STATUS_STYLES[key] || {
      bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe",
      // An unknown status still reads better than a blank pill.
      label: status ? String(status) : "Booked",
    }
  );
};

/* The API sends "YYYY-MM-DD HH:mm:ss" with the time part zeroed — the real time
   lives in `demo_time` as a display string, so only the date is parsed here. */
export const demoDay = (lead) =>
  lead?.demo_date ? dayjs(String(lead.demo_date).slice(0, 10), "YYYY-MM-DD") : null;

export const formatDemoDate = (lead) => {
  const d = demoDay(lead);
  return d?.isValid() ? d.format("DD MMM YYYY") : "—";
};

/** Date + time on one line; the time is skipped when the API has none. */
export const formatDemoWhen = (lead) =>
  `${formatDemoDate(lead)}${lead?.demo_time ? ` · ${lead.demo_time} IST` : ""}`;

/** A booking whose day has passed while it was never held or cancelled. */
export const isDemoOverdue = (lead) => {
  const d = demoDay(lead);
  if (!d?.isValid()) return false;
  const status = String(lead?.demo_status || "").trim().toLowerCase();
  if (status === "done" || status === "cancelled") return false;
  return d.isBefore(dayjs().startOf("day"), "day");
};

/* A cancelled demo keeps Reschedule — that is how it gets revived — but its old
   Teams link is no longer something to join. A finished one offers neither. */
export const canJoinDemo = (lead) => {
  const status = String(lead?.demo_status || "").trim().toLowerCase();
  return (
    hasDemo(lead) &&
    lead?.demo_mode === "video" &&
    Boolean(lead?.demo_meeting_link) &&
    status !== "cancelled" &&
    status !== "done"
  );
};

export const canRescheduleDemo = (lead) =>
  hasDemo(lead) && String(lead?.demo_status || "").trim().toLowerCase() !== "done";

/** Status pill — shared by the card, the list column and the follow-up panel. */
export const DemoStatusPill = ({ lead, size = 11 }) => {
  const s = demoStatusStyle(lead?.demo_status);
  return (
    <span
      style={{
        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        borderRadius: 20, padding: "1px 8px", fontSize: size, fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      📅 {s.label}
    </span>
  );
};

export const OverdueDemoHint = () => (
  <span style={{ fontSize: 10, color: "#b45309", fontWeight: 600, whiteSpace: "nowrap" }}>
    ⚠ Overdue demo
  </span>
);

export const openTeamsMeeting = (lead) => {
  if (!lead?.demo_meeting_link) return;
  window.open(lead.demo_meeting_link, "_blank", "noopener,noreferrer");
};
